// Grid line placement utilities for PdfTableViewer
import { COLUMN_ADDITION_MULTI_PAGE } from "../config/gridLinesConfig";
import { calculateDynamicGridLines } from "../tableManipulation/addRowColumn";
import { convertCanvasToPdfCoordinates } from "./geometryCalculations";
import { getCanvasCoordinates } from "./pdfTableViewerUtils";

/**
 * Calculate table boundaries for new grid line placement
 */
export const calculateTableBoundaries = (
  currentGridLines,
  placingGridLineType,
  pdfCoords
) => {
  if (placingGridLineType === "row") {
    // Calculate horizontal boundaries for new row
    let tableMinX, tableMaxX;

    if (currentGridLines.horizontal && currentGridLines.horizontal.length > 0) {
      tableMinX = Math.min(...currentGridLines.horizontal.map((h) => h.x0));
      tableMaxX = Math.max(...currentGridLines.horizontal.map((h) => h.x1));
    } else {
      // Fallback: use vertical lines extent or table boundaries
      if (currentGridLines.vertical && currentGridLines.vertical.length > 0) {
        tableMinX =
          Math.min(...currentGridLines.vertical.map((v) => v.x0)) - 20;
        tableMaxX =
          Math.max(...currentGridLines.vertical.map((v) => v.x0)) + 20;
      } else {
        tableMinX = pdfCoords.x - 100;
        tableMaxX = pdfCoords.x + 100;
      }
    }

    return { tableMinX, tableMaxX };
  } else if (placingGridLineType === "column") {
    // Calculate vertical boundaries for new column
    let tableMinY, tableMaxY;

    if (currentGridLines.vertical && currentGridLines.vertical.length > 0) {
      tableMinY = Math.min(...currentGridLines.vertical.map((v) => v.y0));
      tableMaxY = Math.max(...currentGridLines.vertical.map((v) => v.y1));
    } else {
      if (
        currentGridLines.horizontal &&
        currentGridLines.horizontal.length > 0
      ) {
        tableMinY =
          Math.min(...currentGridLines.horizontal.map((h) => h.y0)) - 20;
        tableMaxY =
          Math.max(...currentGridLines.horizontal.map((h) => h.y0)) + 20;
      } else {
        tableMinY = pdfCoords.y - 100;
        tableMaxY = pdfCoords.y + 100;
      }
    }

    return { tableMinY, tableMaxY };
  }

  return {};
};

/**
 * Create new grid line based on placement type and coordinates
 */
export const createNewGridLine = (
  placingGridLineType,
  pdfCoords,
  boundaries
) => {
  if (placingGridLineType === "row") {
    const { tableMinX, tableMaxX } = boundaries;
    return {
      x0: tableMinX,
      y0: pdfCoords.y,
      x1: tableMaxX,
      y1: pdfCoords.y,
    };
  } else if (placingGridLineType === "column") {
    const { tableMinY, tableMaxY } = boundaries;
    return {
      x0: pdfCoords.x,
      y0: tableMinY,
      x1: pdfCoords.x,
      y1: tableMaxY,
    };
  }
  return null;
};

/**
 * Add new grid line to existing grid lines structure
 */
export const addGridLineToStructure = (
  dynamicGridLines,
  currentPage,
  placingGridLineType,
  newLine
) => {
  const currentGridLines = dynamicGridLines[currentPage];

  // For multi-page tables, column grid lines should be propagated to all pages
  const newGridLines = { ...dynamicGridLines };
  let newLineIndex = -1;

  if (placingGridLineType === "row") {
    // Rows only affect the current page
    newGridLines[currentPage] = {
      ...currentGridLines,
      horizontal: [...(currentGridLines.horizontal || [])],
      vertical: [...(currentGridLines.vertical || [])],
    };
    newGridLines[currentPage].horizontal.push(newLine);
    newLineIndex = newGridLines[currentPage].horizontal.length - 1;
  } else if (placingGridLineType === "column") {
    // Columns affect all pages - propagate to all pages
    Object.keys(dynamicGridLines).forEach((pageNum) => {
      const pageGridLines = dynamicGridLines[pageNum];
      newGridLines[pageNum] = {
        ...pageGridLines,
        horizontal: [...(pageGridLines.horizontal || [])],
        vertical: [...(pageGridLines.vertical || [])],
      };

      // Calculate height for this specific page based on its existing vertical lines
      let pageTableMinY, pageTableMaxY;

      if (pageGridLines.vertical && pageGridLines.vertical.length > 0) {
        pageTableMinY = Math.min(...pageGridLines.vertical.map((v) => v.y0));
        pageTableMaxY = Math.max(...pageGridLines.vertical.map((v) => v.y1));
      } else if (
        pageGridLines.horizontal &&
        pageGridLines.horizontal.length > 0
      ) {
        pageTableMinY = Math.min(...pageGridLines.horizontal.map((h) => h.y0));
        pageTableMaxY = Math.max(...pageGridLines.horizontal.map((h) => h.y1));
      } else {
        // Fallback: use the original newLine dimensions if no grid lines exist
        pageTableMinY = newLine.y0;
        pageTableMaxY = newLine.y1;
      }

      // Create page-specific vertical line with proper height
      const pageSpecificVerticalLine = {
        x0: newLine.x0,
        y0: pageTableMinY,
        x1: newLine.x1,
        y1: pageTableMaxY,
      };

      // Add the page-specific vertical line to this page
      newGridLines[pageNum].vertical.push(pageSpecificVerticalLine);

      // Sort vertical lines by x position to maintain order
      newGridLines[pageNum].vertical.sort((a, b) => a.x0 - b.x0);
    });

    // Calculate the index in the sorted array (use x0 position only since y0/y1 are now page-specific)
    newLineIndex = newGridLines[currentPage].vertical.findIndex(
      (line) => line.x0 === newLine.x0
    );
  }

  return { newGridLines, newLineIndex };
};

/**
 * Handle grid line placement logic
 */
export const handleGridLinePlacement = (
  event,
  {
    isPlacingGridLine,
    placingGridLineType,
    dynamicGridLines,
    currentPage,
    drawingMode,
    selectedTable,
    tableData,
    pdfPageDimensions,
    canvasRef,
    highlightContainerRef,
    gridLineState,
    drawingState,
    recalculateCellsFromGridLinesWrapper,
  }
) => {
  if (!isPlacingGridLine) {
    return;
  }

  // Ensure grid lines exist, if not create basic structure
  if (!dynamicGridLines?.[currentPage]) {
    const newGridLines = calculateDynamicGridLines(
      selectedTable,
      tableData,
      currentPage,
      pdfPageDimensions,
      !COLUMN_ADDITION_MULTI_PAGE // limitToCurrentPageOnly - use config for grid line placement
    );

    if (newGridLines) {
      gridLineState.setDynamicGridLines(newGridLines);
      // Schedule the placement for the next tick after grid lines are set
      setTimeout(
        () =>
          handleGridLinePlacement(event, {
            isPlacingGridLine,
            placingGridLineType,
            dynamicGridLines: newGridLines,
            currentPage,
            drawingMode,
            selectedTable,
            tableData,
            pdfPageDimensions,
            canvasRef,
            highlightContainerRef,
            gridLineState,
            drawingState,
            recalculateCellsFromGridLinesWrapper,
          }),
        10
      );
      return;
    } else {
      return;
    }
  }

  const canvas = canvasRef.current;
  if (!canvas) {
    return;
  }

  // Get canvas coordinates first
  const canvasCoords = getCanvasCoordinates(
    event,
    canvasRef,
    highlightContainerRef
  );
  if (!canvasCoords) {
    return;
  }

  // Convert canvas coordinates to PDF coordinates
  const pdfCoords = convertCanvasToPdfCoordinates(
    canvasCoords.x,
    canvasCoords.y,
    canvasRef,
    pdfPageDimensions
  );
  if (!pdfCoords) {
    return;
  }

  const currentGridLines = dynamicGridLines[currentPage];

  // Clear any existing original grid lines to prevent stale data
  gridLineState.setOriginalGridLines(null);

  // Store original grid lines BEFORE adding the new line
  const storedOriginalGridLines = JSON.parse(JSON.stringify(dynamicGridLines));
  gridLineState.setOriginalGridLines(storedOriginalGridLines);

  // Calculate boundaries and create new line
  const boundaries = calculateTableBoundaries(
    currentGridLines,
    placingGridLineType,
    pdfCoords
  );
  const newLine = createNewGridLine(placingGridLineType, pdfCoords, boundaries);

  if (!newLine) return;

  // Add new grid line to structure
  const { newGridLines, newLineIndex } = addGridLineToStructure(
    dynamicGridLines,
    currentPage,
    placingGridLineType,
    newLine
  );

  // Apply new grid lines and show immediately
  gridLineState.setDynamicGridLines(newGridLines);

  // Store the current placement type before clearing it
  const currentPlacementType = placingGridLineType;

  // Exit placement mode and immediately enter preview mode for the new line
  gridLineState.setIsPlacingGridLine(false);

  // Disable drawing mode when activating drag grid mode
  if (drawingMode) {
    drawingState.setDrawingMode(null);
  }

  gridLineState.setGridLineDragMode(true);

  // Set up the dragging grid line info for the new line (but don't set as actively dragging yet)
  const draggingInfo = {
    type: currentPlacementType === "row" ? "horizontal" : "vertical",
    index: newLineIndex,
  };

  gridLineState.setDraggingGridLine(draggingInfo);

  // Track this as a pending new gridline for coloring
  gridLineState.setPendingNewGridLines({
    type: draggingInfo.type,
    index: draggingInfo.index,
    page: currentPage,
  });

  // Show the new grid line visually and trigger immediate recalculation
  gridLineState.setIsGridLineInPreview(true);
  gridLineState.setPersistentGridLineTooltip({ show: true });

  // Immediately trigger recalculation and show intermediate modal
  try {
    const newTableData = recalculateCellsFromGridLinesWrapper(
      storedOriginalGridLines, // originalGridLines
      newGridLines // newGridLines
    );

    if (newTableData) {
      // Set up pending changes for the intermediate modal
      gridLineState.setPendingGridLineChanges({
        newTableData,
        newGridLines: newGridLines,
        originalTableData: tableData,
        originalGridLines: storedOriginalGridLines,
        affectedPages: [currentPage],
        splitColumnIndex: newTableData.splitColumnIndex,
        splitRowIndex: newTableData.splitRowIndex,
      });

      // Show intermediate "Grid line moved" modal
      gridLineState.setShowGridLineMoved(true);
    }
  } catch (error) {
    // Silently handle errors - the user can retry placement
  }
};
